/**
 * Images → ESC/POS raster (F-FE-N105).
 *
 * This is what carries the FBR / PRA compliance QR and the tenant logo onto a
 * thermal receipt. Without it a tablet cannot print a legal tax invoice, only a
 * sales slip — which is the difference between a till and a note pad.
 *
 * ── Why Canvas 2D and not the obvious shortcut ──
 *
 * The tempting approach is to rasterise the whole receipt HTML and send one
 * bitmap: every document, logo and QR for free, no second renderer to drift.
 * It does not work. `posterExport.ts` records the reason from the last time
 * someone tried:
 *
 *   "The previous SVG/foreignObject approach hit Chromium's longstanding
 *    security rule that any SVG containing a foreignObject taints the canvas
 *    (crbug.com/294129, intentionally WontFix), which then makes
 *    `canvas.toBlob()` throw a SecurityError."
 *
 * A tainted canvas also blocks `getImageData()`, which is exactly the call this
 * file depends on. So we rasterise IMAGES only — drawing an `<img>` whose src is
 * a `data:` URI does not taint anything — and lay the text out as text.
 */

/** GS v 0: raster bit image. `m=0` is normal density, the widely-supported mode. */
const GS = 0x1d

export interface RasterOptions {
  /** Hard cap in dots. 80mm heads are 576 dots wide, 58mm heads 384. */
  maxWidthDots: number
  /**
   * Hard cap on height in dots (F-FE-N110).
   *
   * Without it the image keeps its source aspect at whatever width it was
   * given, and a square logo scaled to the full 576-dot head prints 576 dots
   * TALL — 72mm of solid artwork before the first line of the receipt. That is
   * what Mizaj got. The HTML has always capped it (`.logo { max-height: 22mm }`)
   * and this renderer simply did not read that half of the box.
   */
  maxHeightDots?: number
  /**
   * Luminance below which a pixel is ink, 0-255.
   *
   * Thermal output is one bit: there is no grey. 0.6-ish keeps a QR's quiet zone
   * clean while still rendering a mid-tone logo, and matters most for the QR —
   * an over-inked symbol bleeds between modules and stops scanning, which is the
   * failure that would make a tax invoice worthless.
   */
  threshold?: number
}

/**
 * Decode an image (any `data:` URI, or a same-origin URL) to ESC/POS raster
 * bytes, scaled to fit `maxWidthDots` and centred by the caller.
 *
 * Returns an empty array when the image cannot be loaded — a receipt missing its
 * logo still prints, and the caller decides whether a missing QR is fatal.
 */
export async function imageToRaster(
  src: string,
  opts: RasterOptions,
): Promise<Uint8Array> {
  const img = await loadImage(src)
  if (!img) return new Uint8Array()

  // Fit INSIDE the box, both axes, keeping the aspect — the same thing
  // `object-fit: contain` does for the HTML. Scaling on width alone is how a
  // square logo came out 72mm tall (F-FE-N110).
  let scale = Math.min(1, opts.maxWidthDots / img.width)
  if (opts.maxHeightDots && opts.maxHeightDots > 0) {
    scale = Math.min(scale, opts.maxHeightDots / img.height)
  }
  // Width must be a multiple of 8 — the format packs 8 horizontal pixels per
  // byte and a partial byte shifts every subsequent row, shearing the image.
  const w = Math.max(8, Math.floor((img.width * scale) / 8) * 8)
  const h = Math.max(1, Math.round(img.height * (w / img.width)))

  const canvas = document.createElement('canvas')
  canvas.width = w
  canvas.height = h
  const ctx = canvas.getContext('2d')
  if (!ctx) return new Uint8Array()

  // White ground first: a transparent PNG (every logo we ship) would otherwise
  // read as black and print a solid block.
  ctx.fillStyle = '#fff'
  ctx.fillRect(0, 0, w, h)
  ctx.drawImage(img, 0, 0, w, h)

  let px: ImageData
  try {
    px = ctx.getImageData(0, 0, w, h)
  } catch {
    // Only reachable if the source tainted the canvas, i.e. someone passed a
    // cross-origin URL. Losing the image beats throwing mid-print.
    return new Uint8Array()
  }

  const threshold = Math.round((opts.threshold ?? 0.6) * 255)
  const bytesPerRow = w / 8
  const body = new Uint8Array(bytesPerRow * h)
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const i = (y * w + x) * 4
      const a = px.data[i + 3]
      // Luma, not a flat average — a mid-green logo and a mid-red one should
      // ink the same way the eye reads them.
      const luma = 0.299 * px.data[i] + 0.587 * px.data[i + 1] + 0.114 * px.data[i + 2]
      const ink = a > 0x40 && luma < threshold
      if (ink) body[y * bytesPerRow + (x >> 3)] |= 0x80 >> (x & 7)
    }
  }

  const header = Uint8Array.from([
    GS, 0x76, 0x30, 0x00,
    bytesPerRow & 0xff, (bytesPerRow >> 8) & 0xff,
    h & 0xff, (h >> 8) & 0xff,
  ])
  const out = new Uint8Array(header.length + body.length)
  out.set(header, 0)
  out.set(body, header.length)
  return out
}

function loadImage(src: string): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    if (!src) {
      resolve(null)
      return
    }
    const img = new Image()
    // Harmless for the data: URIs we actually use, and the right request for a
    // same-origin asset.
    img.crossOrigin = 'anonymous'
    img.onload = () => resolve(img)
    img.onerror = () => resolve(null)
    img.src = src
  })
}
